import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { exhaustiveListOf, StringSearchParams, toNumberOrUndefined, useUrlState } from '@round/utils';
import { influencer, serializeOperatorFilterToApiParam } from '@round/api';
import { getTiktokIDFromIdPath } from '../components/filters/TiktokAudioUrlFilter/helpers';
import {
    parseOperatorFilterFromUrl,
    parseAudienceLocationUrlCondition,
    parseContentTagsFromApi,
    serializeAudienceLocationsToApi,
    serializeContentTagsToApi,
} from '../utils/filters';
import { daysAgo, LAST_BOOKED_OR_ADDED_DEFAULT_DAYS } from '../utils/creatorSearchUtils';
import { DATE_ADDED_COLUMN_ID } from '../components/CreatorExploreTable/helpers';

export type UrlState = {
    search?: string;
    blurbSearch?: string;
    blurbSearchEngine?: string;
    /** Serialized as `operator:val1,val2` (e.g. `and:tiktok,instagram`) or plain `val1,val2` for implicit OR */
    platforms?: string;
    /** Serialized as JSON (e.g. `{"operator":"AND","conditions":["AI","Anime"]}`), with legacy
     * `operator:val1,val2` / plain `val1,val2` URLs still parsed for backward compatibility */
    contentTags?: string;
    locations?: string[];
    minFollowers?: number;
    maxFollowers?: number;
    minAvgViews?: number;
    maxAvgViews?: number;
    minEstimatedCpe?: number;
    maxEstimatedCpe?: number;
    minAvgEngagementRate?: number;
    maxAvgEngagementRate?: number;
    minResponseRate?: number;
    maxAvgDraftTimeInHours?: number;
    // ISO string
    maxLastUsedTime?: string;
    postingFrequency?: string;
    minCost?: number;
    maxCost?: number;
    hasCost?: boolean;
    tiktokAudioIds?: string[];
    spotifyTrackIds?: string[];
    spotifyGenres?: string[];
    spotifyArtistIds?: string[];
    audienceLocations?: string;
    order_by_asc?: string;
    order_by_desc?: string;
    visibilityPreset?: string;
};

const parseUrlParams = (searchParams: StringSearchParams<UrlState>): UrlState => {
    return {
        search: searchParams.search,
        blurbSearch: searchParams.blurbSearch,
        blurbSearchEngine: searchParams.blurbSearchEngine,
        platforms: searchParams.platforms,
        contentTags: searchParams.contentTags,
        locations: searchParams.locations?.split(',').filter(Boolean),
        minFollowers: toNumberOrUndefined(searchParams.minFollowers),
        maxFollowers: toNumberOrUndefined(searchParams.maxFollowers),
        minAvgViews: toNumberOrUndefined(searchParams.minAvgViews),
        maxAvgViews: toNumberOrUndefined(searchParams.maxAvgViews),
        minEstimatedCpe: toNumberOrUndefined(searchParams.minEstimatedCpe),
        maxEstimatedCpe: toNumberOrUndefined(searchParams.maxEstimatedCpe),
        minAvgEngagementRate: toNumberOrUndefined(searchParams.minAvgEngagementRate),
        maxAvgEngagementRate: toNumberOrUndefined(searchParams.maxAvgEngagementRate),
        minResponseRate: toNumberOrUndefined(searchParams.minResponseRate),
        maxAvgDraftTimeInHours: toNumberOrUndefined(searchParams.maxAvgDraftTimeInHours),
        maxLastUsedTime: searchParams.maxLastUsedTime,
        postingFrequency: searchParams.postingFrequency,
        minCost: toNumberOrUndefined(searchParams.minCost),
        maxCost: toNumberOrUndefined(searchParams.maxCost),
        hasCost: searchParams.hasCost === 'true',
        tiktokAudioIds: searchParams.tiktokAudioIds
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        spotifyTrackIds: searchParams.spotifyTrackIds
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        spotifyGenres: searchParams.spotifyGenres
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        spotifyArtistIds: searchParams.spotifyArtistIds
            ?.split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        audienceLocations: searchParams.audienceLocations,
        order_by_asc: searchParams.order_by_asc,
        order_by_desc: searchParams.order_by_desc,
        visibilityPreset: searchParams.visibilityPreset,
    };
};

export const mapToApiParams = (
    urlState: UrlState
): Pick<
    influencer.GetCreatorExploreParams,
    | 'search'
    | 'blurb_search'
    | 'blurb_search_engine'
    | 'platforms'
    | 'content_tags'
    | 'primary_country'
    | 'min_follower_count'
    | 'max_follower_count'
    | 'min_avg_video_views_per_post'
    | 'max_avg_video_views_per_post'
    | 'min_estimated_cpe'
    | 'max_estimated_cpe'
    | 'min_avg_engagement_rate'
    | 'max_avg_engagement_rate'
    | 'min_response_rate'
    | 'max_avg_draft_time_in_hours'
    | 'min_last_used_date'
    | 'posting_frequency'
    | 'min_cost'
    | 'max_cost'
    | 'has_cost'
    | 'has_used_tiktok_audio_tiktok_ids'
    | 'spotify_ids'
    | 'audience_location'
    | 'order_by_asc'
    | 'order_by_desc'
    | 'genres'
    | 'artist_ids'
> => ({
    search: urlState.search,
    blurb_search: urlState.blurbSearch,
    // Only Jev is sent; anything else leaves the API on its mongo default.
    blurb_search_engine: urlState.blurbSearchEngine === 'jev' ? 'jev' : undefined,
    platforms: serializeOperatorFilterToApiParam(parseOperatorFilterFromUrl(urlState.platforms)),
    content_tags: serializeContentTagsToApi(parseContentTagsFromApi(urlState.contentTags)),
    primary_country: urlState.locations?.join(','),
    audience_location: serializeAudienceLocationsToApi(
        parseOperatorFilterFromUrl(urlState.audienceLocations, parseAudienceLocationUrlCondition)
    ),
    min_follower_count: urlState.minFollowers,
    max_follower_count: urlState.maxFollowers,
    min_avg_video_views_per_post: urlState.minAvgViews,
    max_avg_video_views_per_post: urlState.maxAvgViews,
    min_estimated_cpe: urlState.minEstimatedCpe,
    max_estimated_cpe: urlState.maxEstimatedCpe,
    min_response_rate: urlState.minResponseRate ? (urlState.minResponseRate / 100).toString() : undefined,
    min_avg_engagement_rate: urlState.minAvgEngagementRate ? urlState.minAvgEngagementRate / 100 : undefined,
    max_avg_engagement_rate: urlState.maxAvgEngagementRate ? urlState.maxAvgEngagementRate / 100 : undefined,
    max_avg_draft_time_in_hours: urlState.maxAvgDraftTimeInHours ? String(urlState.maxAvgDraftTimeInHours) : undefined,
    min_last_used_date: urlState.maxLastUsedTime,
    posting_frequency: urlState.postingFrequency,
    min_cost: urlState.minCost,
    max_cost: urlState.maxCost,
    has_cost: urlState.hasCost ? 'true' : undefined,
    has_used_tiktok_audio_tiktok_ids: urlState.tiktokAudioIds?.map((id) => getTiktokIDFromIdPath(id)).join(','),
    spotify_ids: urlState.spotifyTrackIds?.join(','),
    genres: urlState.spotifyGenres?.join(','),
    artist_ids: urlState.spotifyArtistIds?.join(','),
    order_by_asc: urlState.order_by_asc,
    order_by_desc: urlState.order_by_desc,
});

const initialState: UrlState = {
    hasCost: true,
};

// Params outside this list (e.g. a removed or renamed filter in an old link) are dropped on init.
const URL_STATE_KEYS = exhaustiveListOf<keyof UrlState>()([
    'search',
    'blurbSearch',
    'blurbSearchEngine',
    'platforms',
    'contentTags',
    'locations',
    'minFollowers',
    'maxFollowers',
    'minAvgViews',
    'maxAvgViews',
    'minEstimatedCpe',
    'maxEstimatedCpe',
    'minAvgEngagementRate',
    'maxAvgEngagementRate',
    'minResponseRate',
    'maxAvgDraftTimeInHours',
    'maxLastUsedTime',
    'postingFrequency',
    'minCost',
    'maxCost',
    'hasCost',
    'tiktokAudioIds',
    'spotifyTrackIds',
    'spotifyGenres',
    'spotifyArtistIds',
    'audienceLocations',
    'order_by_asc',
    'order_by_desc',
    'visibilityPreset',
]);

// Sortable Creator Explore column ids. A sort param holding anything else (e.g. the legacy `created`
// from old links) is dropped on init, so the default sort applies instead of an unmatched column.
const SORTABLE_FIELDS = [
    'metrics.avg_draft_time_in_hours',
    'metrics.response_rate',
    'post_metrics.most_recent_post_date',
    'estimated_cpe',
    'total_follower_count',
    'cost',
    'avg_video_views_per_post',
    'estimated_cpm',
    'avg_engagement_rate',
    'post_metrics.activity_rate',
    DATE_ADDED_COLUMN_ID,
];

const SORT_PARAM_KEYS = ['order_by_asc', 'order_by_desc'] as const;

const getInvalidSortParamKeys = (searchParams: StringSearchParams<UrlState>) =>
    SORT_PARAM_KEYS.filter((key) => {
        const value = searchParams[key];
        return value !== undefined && !SORTABLE_FIELDS.includes(value);
    });

const getValidSearchParams = (searchParams: StringSearchParams<UrlState>): StringSearchParams<UrlState> =>
    omit(pick(searchParams, URL_STATE_KEYS), getInvalidSortParamKeys(searchParams));

export default function useCreatorExploreUrlState() {
    // Not merged onto the incoming url on purpose: a merge would restore the params dropped here.
    const { url, mergeSearchParams } = useUrlState<UrlState>((urlData) => {
        const validSearchParams = getValidSearchParams(urlData.searchParams);
        // Only one sort param may be set, so a sort from the url replaces the default instead of joining it.
        const hasSort = SORT_PARAM_KEYS.some((key) => validSearchParams[key] !== undefined);

        return {
            hash: urlData.hash,
            searchParams: {
                ...initialState,
                ...(hasSort ? {} : { order_by_desc: DATE_ADDED_COLUMN_ID }),
                maxLastUsedTime: daysAgo(LAST_BOOKED_OR_ADDED_DEFAULT_DAYS),
                ...validSearchParams,
            },
        };
    });

    const urlState = parseUrlParams(url.searchParams);

    return {
        urlState,
        setUrlState: mergeSearchParams,
    };
}
